import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, CheckCircle, Package, Loader2, Info, CheckCheck } from 'lucide-react';
import { DownloadStats } from '../../services/review';

interface DownloadModalProps {
  open: boolean;
  onClose: () => void;
  downloadStats: DownloadStats | null;
  totalProcessed: number;
  onDownloadApproved: () => Promise<void>;
  onDownloadAllApproved: () => Promise<void>;
  onDownloadAll: () => Promise<void>;
}

export function DownloadModal({
  open,
  onClose,
  downloadStats,
  totalProcessed,
  onDownloadApproved,
  onDownloadAllApproved,
  onDownloadAll,
}: DownloadModalProps) {
  const [isDownloading, setIsDownloading] = useState<'approved' | 'allApproved' | 'all' | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<string>('');

  const handleDownloadApproved = async () => {
    setIsDownloading('approved');
    setDownloadStatus('Preparing ZIP file...');
    try {
      await onDownloadApproved();
      setDownloadStatus('Download complete!');
      setTimeout(() => {
        onClose();
        setIsDownloading(null);
        setDownloadStatus('');
      }, 1500);
    } catch (error) {
      setDownloadStatus('');
      setIsDownloading(null);
    }
  };

  const handleDownloadAllApproved = async () => {
    setIsDownloading('allApproved');
    setDownloadStatus('Preparing ZIP file...');
    try {
      await onDownloadAllApproved();
      setDownloadStatus('Download complete!');
      setTimeout(() => {
        onClose();
        setIsDownloading(null);
        setDownloadStatus('');
      }, 1500);
    } catch (error) {
      setDownloadStatus('');
      setIsDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloading('all');
    setDownloadStatus('Preparing ZIP file...');
    try {
      await onDownloadAll();
      setDownloadStatus('Download complete!');
      setTimeout(() => {
        onClose();
        setIsDownloading(null);
        setDownloadStatus('');
      }, 1500);
    } catch (error) {
      setDownloadStatus('');
      setIsDownloading(null);
    }
  };

  const pendingDownload = downloadStats?.pendingDownload || 0;
  const alreadyDownloaded = downloadStats?.alreadyDownloaded || 0;
  const totalApproved = downloadStats?.totalApproved || 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isDownloading && !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Download Images
          </DialogTitle>
          <DialogDescription>
            Choose which images you want to download as a ZIP file.
          </DialogDescription>
        </DialogHeader>

        {/* Stats Overview */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center gap-2">
              <Package className="w-4 h-4" />
              Total Processed
            </span>
            <span className="font-medium">{totalProcessed}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center gap-2">
              <CheckCheck className="w-4 h-4 text-emerald-500" />
              Total Approved
            </span>
            <span className="font-medium text-emerald-600">{totalApproved}</span>
          </div>
          <div className="flex items-center justify-between text-sm pl-6 border-l-2 border-emerald-500/30">
            <span className="text-muted-foreground flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              New (not downloaded)
            </span>
            <span className="font-medium text-emerald-500">{pendingDownload}</span>
          </div>
          <div className="flex items-center justify-between text-sm pl-6 border-l-2 border-blue-500/30">
            <span className="text-muted-foreground flex items-center gap-2">
              <Download className="w-4 h-4 text-blue-400" />
              Already Downloaded
            </span>
            <span className="font-medium text-blue-500">{alreadyDownloaded}</span>
          </div>
        </div>

        {/* Explanation */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <div className="flex gap-2">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-200">
              <strong>How it works:</strong> Images go through an approval process before download.
              "Approved (New)" shows images that have been approved but not yet downloaded.
              Once downloaded, they're marked to prevent duplicate downloads.
            </p>
          </div>
        </div>

        {/* Download Status */}
        {isDownloading && downloadStatus && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {downloadStatus}
          </div>
        )}

        {/* Download Options */}
        <div className="space-y-3 pt-2">
          {/* Download New Approved Only */}
          <Button
            className="w-full justify-between h-auto py-3 bg-emerald-600 hover:bg-emerald-700"
            onClick={handleDownloadApproved}
            disabled={isDownloading !== null || pendingDownload === 0}
          >
            <div className="flex flex-col items-start">
              <span className="font-medium">Download New Approved</span>
              <span className="text-xs opacity-80">
                {pendingDownload > 0
                  ? `${pendingDownload} image${pendingDownload !== 1 ? 's' : ''} not yet downloaded`
                  : 'No new approved images'
                }
              </span>
            </div>
            {isDownloading === 'approved' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
          </Button>

          {/* Download All Approved */}
          <Button
            variant="outline"
            className="w-full justify-between h-auto py-3 border-emerald-500/50 hover:bg-emerald-500/10"
            onClick={handleDownloadAllApproved}
            disabled={isDownloading !== null || totalApproved === 0}
          >
            <div className="flex flex-col items-start">
              <span className="font-medium">Download All Approved</span>
              <span className="text-xs text-muted-foreground">
                {totalApproved > 0
                  ? `${totalApproved} image${totalApproved !== 1 ? 's' : ''} (includes already downloaded)`
                  : 'No approved images'
                }
              </span>
            </div>
            {isDownloading === 'allApproved' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <CheckCheck className="w-5 h-5" />
            )}
          </Button>

          {/* Download All Processed */}
          <Button
            variant="outline"
            className="w-full justify-between h-auto py-3"
            onClick={handleDownloadAll}
            disabled={isDownloading !== null || totalProcessed === 0}
          >
            <div className="flex flex-col items-start">
              <span className="font-medium">Download All Processed</span>
              <span className="text-xs text-muted-foreground">
                {totalProcessed > 0
                  ? `${totalProcessed} image${totalProcessed !== 1 ? 's' : ''} (any status)`
                  : 'No processed images'
                }
              </span>
            </div>
            {isDownloading === 'all' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Package className="w-5 h-5" />
            )}
          </Button>
        </div>

        {/* Cancel Button */}
        <div className="flex justify-end pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isDownloading !== null}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
